import { Icon } from '@iconify-icon/react';
import Link from 'next/link';
import { NextSeo } from 'next-seo';

import Footer from '@/components/layouts/Footer';
import Header from '@/components/layouts/Header';
import SubscribeForm from '@/components/modules/Webinar/SubscribeForm';
import {
  pastWebinars,
  plannedSessions,
  SERIES_LENGTH,
  upcomingWebinars,
  type Webinar,
} from '@/components/modules/Webinar/webinars';
import PageLink from '@/utils/PageLink';

/** Listing card for a session that has already run. */
const ReplayCard: React.FC<{ event: Webinar }> = ({ event }) => (
  <article className="grid gap-6 rounded-[28px] border border-gray-200 bg-white p-5 sm:grid-cols-[minmax(0,260px)_1fr] sm:p-6">
    <Link
      href={`/webinars/${event.slug}`}
      aria-label={`Watch ${event.title} ${event.titleAccent ?? ''}`.trim()}
      className="group relative flex aspect-video items-center justify-center overflow-hidden rounded-2xl bg-[#06264b] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary-600">
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_70%_15%,#0055af99,transparent_60%),radial-gradient(circle_at_15%_90%,#d4a84b33,transparent_55%)]"
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,#ffffff0a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0a_1px,transparent_1px)] bg-[size:2rem_2rem]"
      />
      <span
        aria-hidden="true"
        className="relative flex h-14 w-14 items-center justify-center rounded-full bg-white/95 text-primary-600 shadow-xl transition-transform duration-200 group-hover:scale-110">
        <Icon icon="tabler:player-play-filled" className="text-xl" />
      </span>
    </Link>
    <div className="sm:py-1">
      <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#866112]">
        Session {event.session} of {SERIES_LENGTH} · Recording
      </p>
      <h3 className="mt-2.5 text-xl font-bold tracking-tight text-gray-900 sm:text-2xl">
        {event.title}{' '}
        {event.titleAccent && <span className="text-primary-600">{event.titleAccent}</span>}
      </h3>
      <p className="mt-3 text-sm leading-relaxed text-gray-600">{event.summary}</p>
      <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-500">
        <span className="inline-flex items-center gap-1.5">
          <Icon icon="tabler:calendar-event" className="text-base text-primary-600" />
          <time dateTime={event.isoDate}>{event.date}</time>
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Icon icon="tabler:clock" className="text-base text-primary-600" />
          {event.duration}
        </span>
      </div>
      <Link
        href={`/webinars/${event.slug}`}
        className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-500">
        Watch the recording
        <Icon icon="tabler:arrow-right" className="text-base" />
      </Link>
    </div>
  </article>
);

const WebinarsPage: React.FC = () => (
  <div className="bg-white">
    <NextSeo
      title="Webinars"
      description="Free 30-minute webinars from SA Cyber and Chat.co on using AI safely in your business. Watch past sessions in full — no sign-up needed."
      canonical="https://chat.co/webinars"
      openGraph={{
        url: 'https://chat.co/webinars',
        title: 'Chat.co Security Series — Free Webinars',
        description:
          'Practical, 30-minute sessions for small businesses on using AI safely. Watch the recordings free.',
      }}
    />
    <Header />

    {/* ── Hero ── */}
    <section className="relative overflow-hidden bg-[#06264b] text-white">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-32 -top-64 h-[650px] w-[650px] rounded-full bg-[radial-gradient(circle_at_top,#c6a75055,transparent_70%)]"
      />
      <div className="relative mx-auto w-full max-w-7xl px-6 pb-14 pt-14 lg:px-8 lg:pb-20 lg:pt-20">
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#e6ba50]">
          Chat.co Security Series <span className="px-2">·</span> Free
        </p>
        <h1 className="mt-5 max-w-4xl text-4xl font-bold leading-[1.08] tracking-tight sm:text-6xl">
          Webinars for people
          <br />
          <span className="text-[#e6ba50]">who run a business.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-lg leading-relaxed text-blue-100">
          Thirty minutes, plain English, no technical background needed. A six-part series on using
          AI without putting your client data at risk — hosted by SA Cyber, the creators of Chat.co.
        </p>
        <div className="mt-9 flex flex-wrap gap-x-10 gap-y-4 border-t border-white/15 pt-6 text-sm">
          <span>
            ▦{' '}
            <strong className="ml-2">
              {pastWebinars.length} of {SERIES_LENGTH} sessions
            </strong>{' '}
            recorded
          </span>
          <span>
            ◷ <strong className="ml-2">30 minutes</strong> each
          </span>
          <span className="text-blue-200">Free to watch · No sign-up required</span>
        </div>
      </div>
    </section>

    {/* ── Upcoming sessions with a date ── */}
    {upcomingWebinars.length > 0 && (
      <section className="bg-white pt-14 sm:pt-20">
        <div className="mx-auto w-full max-w-7xl px-6 lg:px-8">
          <h2 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">Coming up</h2>
          <div className="mt-7 space-y-4">
            {upcomingWebinars.map((event) => (
              <div
                key={event.id}
                className="flex flex-col gap-4 rounded-[28px] border border-primary-600/20 bg-[#f6f8fb] p-6 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#866112]">
                    Session {event.session} · {event.date}
                  </p>
                  <h3 className="mt-2 text-lg font-semibold text-gray-900">
                    {event.title} {event.titleAccent}
                  </h3>
                  <p className="mt-1 text-sm text-gray-600">
                    {event.time} {event.timeZone} · {event.duration}
                  </p>
                </div>
                {event.registerPath && (
                  <Link
                    href={event.registerPath}
                    className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-primary-600 px-6 py-3 text-sm font-semibold text-white transition hover:bg-primary-500">
                    Save my place
                    <Icon icon="tabler:arrow-right" className="text-base" />
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    )}

    {/* ── Recordings ── */}
    <section className="bg-white py-14 sm:py-20">
      <div className="mx-auto w-full max-w-7xl px-6 lg:px-8">
        <h2 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
          Watch a past session
        </h2>
        <p className="mt-3 max-w-2xl text-base leading-relaxed text-gray-600">
          Every session is recorded and published here in full. No registration, no email wall.
        </p>
        <div className="mt-8 space-y-5">
          {pastWebinars.map((event) => (
            <ReplayCard key={event.id} event={event} />
          ))}
        </div>
      </div>
    </section>

    {/* ── What's next + subscribe ── */}
    <section className="bg-[#f6f8fb] py-14 sm:py-20">
      <div className="mx-auto grid w-full max-w-7xl items-start gap-10 px-6 lg:grid-cols-[1fr_minmax(0,440px)] lg:gap-14 lg:px-8">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
            What’s coming next
          </h2>
          <p className="mt-3 max-w-xl text-base leading-relaxed text-gray-600">
            Same time next month, same 30 minutes. Dates go out by email as each session is
            scheduled.
          </p>
          <ol className="mt-8 overflow-hidden rounded-[28px] border border-gray-200 bg-white">
            {plannedSessions.map((item) => (
              <li
                key={item.session}
                className="flex items-center gap-5 border-b border-gray-100 px-6 py-5 last:border-0">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#eef3f9] text-sm font-bold text-primary-600">
                  {item.session}
                </span>
                <div>
                  <p className="font-semibold text-gray-900">{item.title}</p>
                  <p className="mt-0.5 text-xs uppercase tracking-wide text-gray-400">
                    Date to be announced
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div>
        <SubscribeForm />
      </div>
    </section>

    {/* ── CTA ── */}
    <section className="bg-white py-14 sm:py-20">
      <div className="mx-auto w-full max-w-4xl px-6 lg:px-8">
        <div className="rounded-[28px] bg-primary-600 px-8 py-10 text-center sm:px-12">
          <h2 className="text-2xl font-bold text-white sm:text-3xl">
            Want this walked through on your own documents?
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-base text-blue-100">
            We can show your team the four safeguards inside your own workspace, in about twenty
            minutes. Bring one question from your business.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href={PageLink.demo}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-medium text-primary-600 hover:bg-blue-50 sm:w-auto">
              Book a 20-minute walkthrough
              <Icon icon="tabler:arrow-right" className="text-lg" />
            </Link>
            <Link
              href={PageLink.login}
              className="inline-flex w-full items-center justify-center rounded-full border border-white/40 px-6 py-3 text-sm font-medium text-white hover:bg-white/10 sm:w-auto">
              Start a 14-day free trial
            </Link>
          </div>
          <p className="mt-6 text-sm text-blue-100">
            Questions in the meantime?{' '}
            <a
              href="mailto:hello@chat.co"
              className="font-medium text-white underline underline-offset-4">
              hello@chat.co
            </a>
          </p>
        </div>
      </div>
    </section>

    <Footer />
  </div>
);

export default WebinarsPage;
